import { Injectable, OnDestroy } from '@angular/core';
import { LaunchDarklyService } from '@precisely/prism-ng/launch-darkly';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Known LaunchDarkly boolean flag keys used in this application.
 * Add new flag keys here as they are created in LaunchDarkly.
 */
export const LD_FLAGS = {
  PDX_PORTAL_MODERNIZATION: 'PdxPortalModernizationUI',
} as const;

/**
 * Reactive service for accessing LaunchDarkly boolean feature flags.
 *
 * - Maintains a per-flag BehaviorSubject so components can subscribe to live values.
 * - Listens to the LD 'change' event (fired after `identify()` completes) and
 *   automatically refreshes all registered flag subjects.
 *
 * Usage in a component:
 * ```typescript
 * // Inject:
 * constructor(private _ldFlagService: LaunchDarklyFlagService) {}
 *
 * // Expose to template:
 * isPdxModernizationEnabled$ = this._ldFlagService.getFlag$(LD_FLAGS.PDX_PORTAL_MODERNIZATION);
 *
 * // Template:
 * *ngIf="isPdxModernizationEnabled$ | async"
 * ```
 *
 * To add a new flag — just add the key to `LD_FLAGS` above and call `getFlag$()`.
 */
@Injectable({ providedIn: 'root' })
export class LaunchDarklyFlagService implements OnDestroy {
  private _flagSubjects = new Map<string, BehaviorSubject<boolean>>();
  private _changeHandler = () => this._refreshAll();

  constructor(private _ldService: LaunchDarklyService) {
    // Pre-initialize ONLY the flags declared in LD_FLAGS — no other flags are tracked.
    Object.values(LD_FLAGS).forEach((key) => {
      this._flagSubjects.set(key, new BehaviorSubject<boolean>(this._ldService.variation<boolean>(key, false)));
    });
    // One global listener — fires when identify() completes and LD evaluates new context
    this._ldService.client.on('change', this._changeHandler);
  }

  ngOnDestroy(): void {
    this._ldService.client.off('change', this._changeHandler);
  }

  /**
   * Returns a reactive Observable for the given flag key.
   * Emits the current value immediately (BehaviorSubject) and re-emits
   * whenever LaunchDarkly fires a 'change' event (e.g. after identify()).
   * Only flags declared in LD_FLAGS are tracked — unknown keys log a warning and return false.
   */
  getFlag$(key: string): Observable<boolean> {
    if (!this._flagSubjects.has(key)) {
      console.warn(`[LaunchDarklyFlagService] Unknown flag key: "${key}". Add it to LD_FLAGS.`);
      return new BehaviorSubject<boolean>(false).asObservable();
    }
    return this._flagSubjects.get(key)!.asObservable();
  }

  /**
   * Returns the current synchronous value for a flag (no reactivity).
   * Useful in ngOnInit when you only need the value once.
   */
  variation<T>(key: string, defaultValue: T): T {
    return this._ldService.variation<T>(key, defaultValue);
  }

  private _refreshAll(): void {
    this._flagSubjects.forEach((subject, key) => {
      subject.next(this._ldService.variation<boolean>(key, false));
    });
  }
}
